import React, {
  forwardRef,
  useRef,
  useEffect,
  useImperativeHandle,
  useMemo,
} from "react";
import { Container } from "@pixi/react";
import { useSelector } from "react-redux";
import { Settings } from "../../../settings";
import { selectBet, selectFreeRoundsActive, selectFreeRoundsBet } from "../../../State/selectors";
import OverlayReelController from "./OverlayReelController";
import { safeInit } from "../../../utilities";
import { FEATURES_CONFIG } from "../../../featuresConfig";

/**
 * One overlay reel: owns the reel's OverlayReelController (win / appear /
 * expanding wild spines) and hosts the full-reel expanding wild. The 3 symbol
 * cells are rendered by OverlayReelSet and arrive as `symbolRefs`. Native
 * units, so it never resizes.
 */
const OverlayReel = forwardRef(
  ({ index, symbolWidth, symbolHeight, reelSpacing, symbolRefs }, ref) => {
    const overlayReelControllerRef = useRef(null);
    const reelContainerRef = useRef(null);
    // Latest bet, readable from the mount effect's stale closure. The
    // controller can be built a frame or more after mount (see the readiness
    // retry below), and the [bet] effect below bails out while the controller
    // is still null — so without this a deferred build would freeze the
    // mount-time bet into every coin value text.
    const betRef = useRef(null);
    
    // Use free rounds bet when active, otherwise use regular bet - memoized
    const freeRoundsActive = useSelector(selectFreeRoundsActive);
    const freeRoundsBet = useSelector(selectFreeRoundsBet);
    const regularBet = useSelector(selectBet);
    const bet = useMemo(
      () =>
        FEATURES_CONFIG.FREE_ROUNDS.ENABLED && freeRoundsActive && freeRoundsBet
          ? freeRoundsBet
          : regularBet,
      [freeRoundsActive, freeRoundsBet, regularBet]
    );

    // Memoize container X position
    const containerX = useMemo(
      () => reelSpacing * (index - (Settings.REEL_COUNT - 1) / 2),
      [reelSpacing, index]
    );

    // Initialize the controller once.
    //
    // The 3 symbol containers arrive as a prop. They should be attached before
    // this effect runs (refs attach in the commit phase, and OverlayReelSet
    // emits the symbols ahead of the reels), but the controller calls
    // overlaySymbol.addChild() while it is built, and a null there would leave
    // the reel with no spines, so retry on rAF instead of assuming.
    useEffect(() => {
      let rafId = null;

      const tryInit = () => {
        rafId = null;
        if (overlayReelControllerRef.current) return;

        // Counted explicitly rather than with .every() — the refs arrive in a
        // `new Array(SYMBOLS_VISIBLE)`, and every()/filter() SKIP holes in a
        // sparse array, so `.every(Boolean)` reports true on an array that is
        // completely empty. That would wave through a controller with
        // undefined cells and leave the reel with no spines at all.
        let attachedCount = 0;
        for (let i = 0; i < Settings.SYMBOLS_VISIBLE; i++) {
          if (symbolRefs?.[i]) attachedCount++;
        }

        const ready =
          reelContainerRef.current &&
          attachedCount === Settings.SYMBOLS_VISIBLE;

        if (!ready) {
          rafId = requestAnimationFrame(tryInit);
          return;
        }

        safeInit(() => {
          overlayReelControllerRef.current = new OverlayReelController(
            symbolRefs,
            index,
            symbolWidth,
            symbolHeight,
            reelContainerRef.current,
            betRef.current ?? bet
          );
        });
      };

      tryInit();

      return () => {
        if (rafId !== null) cancelAnimationFrame(rafId);
        if (overlayReelControllerRef.current) {
          overlayReelControllerRef.current.cleanup();
          overlayReelControllerRef.current = null;
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Bet update: refresh coin values when the bet changes
    useEffect(() => {
      // Tracked unconditionally: a controller built later reads this instead of
      // the mount effect's captured bet.
      betRef.current = bet;
      if (!overlayReelControllerRef.current) return;
      overlayReelControllerRef.current.updateBet(bet);
    }, [bet]);

    // Imperative overlay reel controls
    useImperativeHandle(ref, () => ({
      getOverlayReelController: () => overlayReelControllerRef.current,

      showWinAnimation: (symbolIndex, symbolId, animationType = "win") => {
        overlayReelControllerRef.current?.showWinAnimation(
          symbolIndex,
          symbolId,
          animationType
        );
      },

      showAppearAnimation: (symbolIndex, symbolId) => {
        overlayReelControllerRef.current?.showAppearAnimation(
          symbolIndex,
          symbolId
        );
      },

      showAppearAnimationAtPosition: (symbolIndex, symbolId) => {
        overlayReelControllerRef.current?.showAppearAnimationAtPosition(
          symbolIndex,
          symbolId
        );
      },

      hideWinAnimation: (symbolIndex) => {
        overlayReelControllerRef.current?.hideWinAnimation(symbolIndex);
      },

      hideAllAnimations: () => {
        overlayReelControllerRef.current?.hideAllAnimations();
      },

      hideAllAnimationsExcept: (upcomingPositions) => {
        overlayReelControllerRef.current?.hideAllAnimationsExcept(
          upcomingPositions
        );
      },

      replaceVisibleSymbols: (targetSymbolId) => {
        overlayReelControllerRef.current?.replaceVisibleSymbols(targetSymbolId);
      },

      playExpandingWildFullReel: () => {
        overlayReelControllerRef.current?.playExpandingWildFullReel();
      },

      playExpandingWildFullReelWin: () => {
        overlayReelControllerRef.current?.playExpandingWildFullReelWin();
      },

      hideExpandingWildFullReel: () => {
        overlayReelControllerRef.current?.hideExpandingWildFullReel();
      },

      forceStopExpandingWildFullReel: () => {
        overlayReelControllerRef.current?.forceStopExpandingWildFullReel();
      },

      playExpandingWildDarkAnimation: () => {
        overlayReelControllerRef.current?.playExpandingWildDarkAnimation();
      },

      playExpandingWildWinAnimation: () => {
        overlayReelControllerRef.current?.playExpandingWildWinAnimation();
      },
    }));

    // Holds only the expanding wild spine: it is full-reel height, so it
    // cannot live in a symbol cell (the symbols are in OverlayReelSet's flat
    // container). Placed at the column's x, so the spine's reel-local
    // coordinates (position.set(0,0), scaleData.x) hold.
    return (
      <Container
        ref={reelContainerRef}
        name={`spine_overlay_ew_${index}`}
        x={containerX}
        zIndex={15}
      />
    );
  }
);

export default OverlayReel;
